import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { buildMatchAnalysis } from '../../utils/matchAnalysis.js';
import {
  resolvePlayerName,
  extractAttemptedPasses,
  extractCompletedPasses,
  toFiniteNumber
} from '../../utils/gameStatsHelpers';
import computePlayerRatingsForMatch from './playerRatingsShared';
import { sendHaxGptChat } from '../../utils/nimClient';

const SYSTEM_PROMPT = 'You are Hax GPT, a constructive Haxball coach. Focus on the selected player, explain replay stats clearly, and offer actionable guidance. Use friendly, simple words—say “lost the ball” instead of “turnover”, “wins” instead of “success rate”. Structure answers with short sections separated by blank lines, use bullet points when helpful, and highlight key callouts with **bold** text. It is okay to add emojis sparingly when they help the player remember a point. If a user asks about pass accuracy, direct them to the in-app Player Stats table because exact attempts are sourced there; do not invent or restate pass accuracy percentages. Keep answers on-topic for performance, strategy, teamwork, and understanding the provided numbers. If a request is outside coaching, politely redirect back to match insights.';

const DEFAULT_FIELD_WIDTH = 840;
const DEFAULT_FIELD_HEIGHT = 420;

function normalizeRoster(list) {
  const names = [];
  const seen = new Set();
  if (!Array.isArray(list)) return names;
  list.forEach((entry) => {
    const name = resolvePlayerName(entry) || (typeof entry === 'string' ? entry : null);
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(trimmed);
  });
  return names;
}

function formatNumber(value, digits = 1) {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(digits) : '0';
}

function formatPercent(value, digits = 1) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0%';
  return `${(num * 100).toFixed(digits)}%`;
}

function formatCount(value) {
  return formatNumber(value, 0);
}

function clampRatio(value) {
  if (!Number.isFinite(value)) return null;
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

function formatPercentOrNA(value, digits = 1) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 'n/a';
  return `${(num * 100).toFixed(digits)}%`;
}

function clampValue(value, min, max) {
  if (!Number.isFinite(value)) return null;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function resolveFieldSize(metrics = {}) {
  const width = Number(metrics.positionFieldWidth);
  const height = Number(metrics.positionFieldHeight);
  return {
    width: Number.isFinite(width) && width > 0 ? width : DEFAULT_FIELD_WIDTH,
    height: Number.isFinite(height) && height > 0 ? height : DEFAULT_FIELD_HEIGHT
  };
}

function buildPositionSnapshot(summary) {
  if (!summary) return null;
  const metrics = summary.metrics || {};
  const { width, height } = resolveFieldSize(metrics);
  const halfWidth = width / 2 || 1;
  const halfHeight = height / 2 || 1;
  const orientation = summary.team === 'blue' ? -1 : 1;
  const avgX = Number(metrics.avgPosX);
  const avgY = Number(metrics.avgPosY);
  const depth = clampValue((avgX / halfWidth) * orientation, -2, 2);
  const lateral = clampValue(avgY / halfHeight, -2, 2);
  const minX = Number(metrics.positionMinX);
  const maxX = Number(metrics.positionMaxX);
  const minY = Number(metrics.positionMinY);
  const maxY = Number(metrics.positionMaxY);
  const spanX = Number.isFinite(minX) && Number.isFinite(maxX) ? clampValue((maxX - minX) / width, 0, 2) : null;
  const spanY = Number.isFinite(minY) && Number.isFinite(maxY) ? clampValue((maxY - minY) / height, 0, 2) : null;
  const samples = Number(metrics.positionSamples) || 0;
  const wideShare = Number(metrics.positionWideShare) || 0;
  const cornerShare = Number(metrics.positionCornerShare) || 0;
  const topShare = Number(metrics.positionTopShare) || 0;
  const bottomShare = Number(metrics.positionBottomShare) || 0;
  const topCornerShare = Number(metrics.positionTopCornerShare) || 0;
  const bottomCornerShare = Number(metrics.positionBottomCornerShare) || 0;
  return {
    position: summary.position || summary.role || 'N/A',
    depth,
    lateral,
    spanX,
    spanY,
    samples,
    wideShare,
    cornerShare,
    topShare,
    bottomShare,
    topCornerShare,
    bottomCornerShare
  };
}

function describeDepth(depth) {
  if (!Number.isFinite(depth)) return 'depth unclear';
  if (depth <= -0.6) return 'stayed deep in the defensive third';
  if (depth <= -0.2) return 'operated mostly in the defensive half';
  if (depth < 0.2) return 'hovered around midfield';
  if (depth < 0.6) return 'regularly pushed into the attacking half';
  return 'played very high near the opponent goal';
}

function describeLateral(lateral) {
  if (!Number.isFinite(lateral) || Math.abs(lateral) < 0.15) return 'central lanes';
  if (lateral < 0) return 'left-side lanes';
  return 'right-side lanes';
}

function formatPositionLine(summary) {
  const snapshot = buildPositionSnapshot(summary);
  if (!snapshot) return null;
  const {
    position,
    depth,
    lateral,
    spanX,
    spanY,
    samples,
    wideShare,
    cornerShare,
    topShare,
    bottomShare,
    topCornerShare,
    bottomCornerShare
  } = snapshot;
  const depthText = describeDepth(depth);
  const lateralText = describeLateral(lateral);
  const spanText = Number.isFinite(spanX) ? `${formatNumber(spanX * 100, 0)}% field width` : 'limited width span';
  const verticalSpanText = Number.isFinite(spanY) ? `${formatNumber(spanY * 100, 0)}% vertical span` : 'limited vertical span';
  const touchesText = samples > 0 ? `${samples} tracked touches` : 'few tracked touches';
  const wideText = `${formatNumber(wideShare * 100, 0)}% near the walls`;
  const cornerText = cornerShare > 0 ? `, ${formatNumber(cornerShare * 100, 0)}% from attacking corners` : '';
  let laneFocus = '';
  if (topShare > bottomShare + 0.05) {
    laneFocus = ` Favoured upper lanes (${formatNumber(topShare * 100, 0)}%).`;
  } else if (bottomShare > topShare + 0.05) {
    laneFocus = ` Favoured lower lanes (${formatNumber(bottomShare * 100, 0)}%).`;
  } else {
    laneFocus = ' Balanced between upper and lower lanes.';
  }
  const rotationNote = cornerShare >= 0.1 && Math.abs(lateral || 0) < 0.15
    ? ' Joined central battles after wall plays (normal when supporting a winger).' 
    : '';
  const cornerFocus = cornerShare >= 0.1
    ? ` Corner split: ${formatNumber(topCornerShare * 100, 0)}% upper vs ${formatNumber(bottomCornerShare * 100, 0)}% lower corners.`
    : '';
  let supportNote = '';
  if (position === 'CB' && wideShare >= 0.18) {
    supportNote = ' Wide coverage helped the winger lock down the wall. 👍';
  }
  return `- Positioning: Assigned **${position}**, ${depthText}, leaning towards ${lateralText}, covering about ${spanText} and ${verticalSpanText} with ${touchesText}; ${wideText}${cornerText}.${laneFocus}${rotationNote}${cornerFocus} ${supportNote}`;
}

function formatTeamShapeLines(teamSummaries, teamKey) {
  if (!Array.isArray(teamSummaries) || !teamSummaries.length) return null;
  const lineup = teamSummaries
    .filter((summary) => (summary.team === teamKey))
    .map((summary) => `${summary.nick} (${summary.position || summary.role || 'N/A'})`);
  if (!lineup.length) return null;
  return `- Team shape (${teamKey.toUpperCase()}): ${lineup.join(', ')}.`;
}

function formatWingSummaryLines(teamSummaries, teamKey, label) {
  if (!Array.isArray(teamSummaries) || !teamSummaries.length) return null;
  const wings = teamSummaries.filter((summary) => summary.team === teamKey && (summary.position === 'LW' || summary.position === 'RW'));
  if (!wings.length) return null;
  const lines = wings.map((summary) => {
    const metrics = summary.metrics || {};
    const goals = formatCount(metrics.goals);
    const assists = formatCount(metrics.assists);
    const shots = `${formatCount(metrics.shotsOnTarget)}/${formatCount(metrics.shotsTotal)}`;
    const wide = formatPercentOrNA(metrics.positionWideShare, 0);
    const corners = formatPercentOrNA(metrics.positionCornerShare, 0);
    const counter = formatCount(metrics.counterRuns);
    return `  • ${summary.nick}: goals ${goals}, assists ${assists}, shots ${shots}, time wide ${wide}, corner fights ${corners}, fast breaks ${counter}.`;
  });
  return [`- Wing snapshot (${label}):`, ...lines].join('\n');
}

function computePassStats(summary, derived) {
  const metrics = summary?.metrics || {};
  const playerRaw = summary?.player || {};

  const firstFinite = (list, fallback = null) => {
    for (const value of list) {
      const num = toFiniteNumber(value);
      if (num !== null) return num;
    }
    return fallback;
  };

  const attemptsFromRaw = extractAttemptedPasses(playerRaw, null);
  const attemptsCandidate = firstFinite([
    metrics.passesAttempted,
    attemptsFromRaw,
    playerRaw.passesAttempted,
    playerRaw.passAttempts,
    playerRaw.passes_attempted,
    playerRaw.stats?.passes?.attempted,
    playerRaw.stats?.passes_attempted
  ], null);

  const completedFromRaw = extractCompletedPasses(playerRaw, null);
  const completedCandidate = firstFinite([
    metrics.passesCompleted,
    completedFromRaw,
    playerRaw.passesCompleted,
    playerRaw.passes_completed,
    playerRaw.stats?.passes?.completed,
    playerRaw.stats?.passes_completed
  ], null);

  let attempts = Number.isFinite(attemptsCandidate) ? attemptsCandidate : null;
  let attemptsSource = Number.isFinite(attemptsCandidate) ? 'direct' : 'unknown';
  let completed = Number.isFinite(completedCandidate) ? completedCandidate : null;
  let completedSource = Number.isFinite(completedCandidate) ? 'direct' : 'unknown';

  const passesMapCount = toFiniteNumber(metrics.passesCompleted);
  const kicksCandidates = [
    metrics.kicksTotal,
    playerRaw.kicks,
    playerRaw.totalKicks,
    playerRaw.kickCount,
    playerRaw.stats?.kicks
  ];
  const kicksTotal = firstFinite(kicksCandidates, 0) || 0;
  const shotsOnTargetCandidates = [
    metrics.shotsOnTarget,
    playerRaw.shotsOnTarget,
    playerRaw.shots_target,
    playerRaw.stats?.shots?.onTarget,
    playerRaw.stats?.shots_onTarget
  ];
  const shotsOnTarget = firstFinite(shotsOnTargetCandidates, 0) || 0;

  if (!Number.isFinite(completed) && Number.isFinite(metrics.passesCompleted)) {
    completed = metrics.passesCompleted;
    completedSource = 'metrics';
  }

  if (!Number.isFinite(attempts) && Number.isFinite(metrics.passesAttempted)) {
    attempts = metrics.passesAttempted;
    attemptsSource = 'metrics';
  }

  if (!Number.isFinite(attempts) && Number.isFinite(completed) && Number.isFinite(derived?.passAccuracy) && derived.passAccuracy > 0) {
    attempts = completed / derived.passAccuracy;
    attemptsSource = 'derived';
  }

  if (!Number.isFinite(completed) && Number.isFinite(attempts) && Number.isFinite(derived?.passAccuracy)) {
    completed = attempts * derived.passAccuracy;
    completedSource = 'derived';
  }

  const inferredAttempts = Math.max(
    Number.isFinite(completed) ? completed : 0,
    Math.max(0, kicksTotal - shotsOnTarget),
    Number.isFinite(passesMapCount) ? passesMapCount : 0
  );
  if (!Number.isFinite(attempts) || attempts <= 0) {
    attempts = inferredAttempts;
    attemptsSource = 'inferred';
  }

  attempts = Number.isFinite(attempts) ? Math.max(0, Math.round(attempts)) : 0;
  completed = Number.isFinite(completed) ? Math.max(0, Math.round(completed)) : 0;

  if (attempts > 0 && completed > attempts) {
    attempts = completed;
    attemptsSource = attemptsSource === 'inferred' ? 'inferred' : 'adjusted';
  }

  let ratio = null;
  const directAccuracy = toFiniteNumber(metrics.passAccuracy);
  const directAccuracyPct = toFiniteNumber(metrics.passAccuracyPct);
  const derivedAccuracy = toFiniteNumber(derived?.passAccuracy);

  if (directAccuracy !== null && directAccuracy > 0) {
    ratio = clampRatio(directAccuracy);
  } else if (directAccuracyPct !== null && directAccuracyPct > 0) {
    ratio = clampRatio(directAccuracyPct / 100);
  } else if (derivedAccuracy !== null && derivedAccuracy > 0) {
    ratio = clampRatio(derivedAccuracy);
  } else if (attempts > 0) {
    ratio = clampRatio(completed / attempts);
  }

  if (Number.isFinite(ratio)) {
    // Avoid presenting a 100% completion rate; cap slightly below perfection.
    ratio = Math.min(ratio, 0.999);
  }

  const hasExplicitAttempts = Number.isFinite(attemptsFromRaw) && attemptsFromRaw > 0;
  const hasExplicitAccuracy = (directAccuracy !== null && directAccuracy > 0) || (directAccuracyPct !== null && directAccuracyPct > 0);
  const isReliable = hasExplicitAttempts || hasExplicitAccuracy;
  if (!isReliable) {
    ratio = null;
  }

  return {
    attempts,
    completed,
    ratio,
    isReliable,
    completedSource,
    inferred: attemptsSource === 'inferred' || attemptsSource === 'adjusted'
  };
}

export default function HaxGPTPanel() {
  const matches = useSelector((state) => state.gameStats.matches) || [];
  const mtc = useSelector((state) => state.gameStats.selectedMatch) || 0;
  const redTeamNames = useSelector((state) => state.gameStats.redTeamNames);
  const blueTeamNames = useSelector((state) => state.gameStats.blueTeamNames);
  const match = matches[mtc] || null;

  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const renderInlineSegments = useCallback((text, keyPrefix = 'inline') => {
    const raw = String(text ?? '');
    if (!raw) return null;
    const parts = raw.split(/(\*\*[^*]+?\*\*)/g);
    return parts.filter(Boolean).map((segment, idx) => {
      const key = `${keyPrefix}-${idx}`;
      if (segment.startsWith('**') && segment.endsWith('**') && segment.length > 4) {
        return (
          <Box component="strong" key={key} sx={{ fontWeight: 700 }}>
            {segment.slice(2, -2)}
          </Box>
        );
      }
      return <React.Fragment key={key}>{segment}</React.Fragment>;
    });
  }, []);

  const renderMessageContent = useCallback((rawContent, keyPrefix = 'msg') => {
    const normalized = String(rawContent ?? '').replace(/\r\n/g, '\n').trim();
    if (!normalized) return null;
    const blocks = normalized.split(/\n{2,}/);
    const elements = [];

    blocks.forEach((block, blockIdx) => {
      const blockKey = `${keyPrefix}-block-${blockIdx}`;
      const rawLines = block.split('\n');
      const meaningfulLines = rawLines.map((line) => line.trim()).filter(Boolean);
      if (!meaningfulLines.length) return;

      let remainingLines = [...meaningfulLines];
      const headingMatch = remainingLines[0].match(/^(#{1,6})\s+(.*)$/);
      if (headingMatch) {
        const level = headingMatch[1].length;
        const headingText = headingMatch[2];
        const variant = level <= 2 ? 'subtitle1' : 'subtitle2';
        elements.push(
          <Typography
            key={`${blockKey}-heading`}
            variant={variant}
            component="h3"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'none',
              mt: blockIdx === 0 ? 0 : 1.6,
              mb: remainingLines.length > 1 ? 0.8 : 0.6
            }}
          >
            {renderInlineSegments(headingText, `${blockKey}-heading`)}
          </Typography>
        );
        remainingLines = remainingLines.slice(1);
      }

      if (!remainingLines.length) return;

      const isList = remainingLines.every((line) => /^[-*•]\s+/.test(line));
      if (isList) {
        elements.push(
          <Box
            component="ul"
            key={`${blockKey}-ul`}
            sx={{ listStyle: 'disc', pl: 3, m: 0, mb: blockIdx === blocks.length - 1 ? 0 : 1.2, '& li': { pb: 0.3 } }}
          >
            {remainingLines.map((line, lineIdx) => {
              const cleaned = line.replace(/^[-*•]\s+/, '').trim();
              return (
                <Box
                  component="li"
                  key={`${blockKey}-li-${lineIdx}`}
                  sx={{
                    '& strong': { fontWeight: 700 },
                    lineHeight: 1.6,
                    pb: lineIdx === remainingLines.length - 1 ? 0 : 0.3
                  }}
                >
                  {renderInlineSegments(cleaned, `${blockKey}-li-${lineIdx}`)}
                </Box>
              );
            })}
          </Box>
        );
        return;
      }

      const paragraphKey = `${blockKey}-p`;
      elements.push(
        <Typography
          variant="body2"
          component="p"
          key={paragraphKey}
          sx={{
            lineHeight: 1.6,
            mb: blockIdx === blocks.length - 1 ? 0 : 1.2,
            '& strong': { fontWeight: 700 }
          }}
        >
          {remainingLines.map((line, lineIdx) => (
            <React.Fragment key={`${paragraphKey}-line-${lineIdx}`}>
              {renderInlineSegments(line, `${paragraphKey}-line-${lineIdx}`)}
              {lineIdx < remainingLines.length - 1 ? <br /> : null}
            </React.Fragment>
          ))}
        </Typography>
      );
    });

    return elements;
  }, [renderInlineSegments]);

  const analysis = useMemo(() => {
    if (!match) return null;
    try {
      const base = buildMatchAnalysis(match);
      const ratings = computePlayerRatingsForMatch(match, base);
      return { ...base, playerSummaries: base.playerSummaries.map(summary => ({
        ...summary,
        ruleRating: ratings.ratingsByNick[summary.nick.trim().toLowerCase()]?.rating ?? null
      })) };
    } catch (_) {
      return null;
    }
  }, [match]);

  const playerSummaries = useMemo(() => {
    if (!analysis || !Array.isArray(analysis.playerSummaries)) return new Map();
    const map = new Map();
    analysis.playerSummaries.forEach((summary) => {
      if (!summary || !summary.nick) return;
      map.set(summary.nick, summary);
    });
    return map;
  }, [analysis]);

  const redPlayers = useMemo(() => normalizeRoster(match?.redTeam), [match?.redTeam]);
  const bluePlayers = useMemo(() => normalizeRoster(match?.blueTeam), [match?.blueTeam]);

  const selectedSummary = useMemo(() => {
    if (!selectedPlayer) return null;
    return playerSummaries.get(selectedPlayer) || null;
  }, [playerSummaries, selectedPlayer]);

  const teamDisplayNames = useMemo(() => {
    const redList = Array.isArray(redTeamNames) ? redTeamNames : [];
    const blueList = Array.isArray(blueTeamNames) ? blueTeamNames : [];
    return {
      red: redList[mtc] || 'Red',
      blue: blueList[mtc] || 'Blue'
    };
  }, [blueTeamNames, mtc, redTeamNames]);

  const teamPlayerSummaries = useMemo(() => {
    if (!analysis || !Array.isArray(analysis.playerSummaries)) return [];
    return analysis.playerSummaries.filter((summary) => {
      if (!summary || typeof summary.nick !== 'string') return false;
      const team = typeof summary.team === 'string' ? summary.team.toLowerCase() : '';
      return team === 'red' || team === 'blue';
    });
  }, [analysis]);

  const getDerivedFor = useCallback((nick) => {
    if (!analysis || !analysis.derivedByNick) return null;
    const source = analysis.derivedByNick;
    if (source instanceof Map) return source.get(nick) || null;
    if (typeof source === 'object' && source !== null) return source[nick] || null;
    return null;
  }, [analysis]);

  const matchContext = useMemo(() => {
    if (!match || !selectedPlayer) return '';
    const scoreRed = Number(match.scoreRed ?? 0);
    const scoreBlue = Number(match.scoreBlue ?? 0);
    const minutes = Number(match.minutes ?? (match.gameTicks || 0) / 3600) || 0;
    const summary = playerSummaries.get(selectedPlayer);
    if (!summary) {
      return [
        `Match result: ${teamDisplayNames.red} ${formatNumber(scoreRed, 0)} - ${formatNumber(scoreBlue, 0)} ${teamDisplayNames.blue}.`,
        `Duration: ${formatNumber(minutes, 1)} minutes.`,
        `Player ${selectedPlayer} was not found in the advanced summary list.`
      ].join('\n');
    }

    const teamKey = typeof summary.team === 'string' && summary.team.toLowerCase() === 'blue' ? 'blue' : 'red';
    const opponentKey = teamKey === 'red' ? 'blue' : 'red';
    const friendlyTeamName = teamDisplayNames[teamKey];
    const opponentTeamName = teamDisplayNames[opponentKey];
    const metrics = summary.metrics || {};
    const derived = getDerivedFor(summary.nick);
    const passStats = computePassStats(summary, derived);
    const passAttempts = Number.isFinite(passStats.attempts) ? passStats.attempts : Number(metrics.passesAttempted) || 0;
    const passesCompleted = Number.isFinite(passStats.completed) ? passStats.completed : Number(metrics.passesCompleted) || 0;
    const gkMinutes = Number.isFinite(Number(metrics.gkTicks)) ? Number(metrics.gkTicks) / 3600 : 0;
    const depthImpact = Number(metrics.depthImpactScore);

    const passesForward = formatCount(metrics.passesForward);
    const passesLateral = formatCount(metrics.passesLateral);
    const passesBackward = formatCount(metrics.passesBackward);
    const duelWinRateRatio = derived?.duelWinRate ?? (Number(metrics.duelsTotal) > 0 ? Number(metrics.duelsWon) / Number(metrics.duelsTotal) : null);
    const duelWinRate = formatPercentOrNA(duelWinRateRatio, 1);
    const pressureReliefPct = formatPercentOrNA(derived?.pressureRelief ?? (Number(metrics.kicksTotal) > 0 ? (Number(metrics.kicksTotal) - Number(metrics.pressuredKicks || 0)) / Number(metrics.kicksTotal) : null), 1);
    const pressureRatePct = formatPercentOrNA(derived?.pressureRate ?? (Number(metrics.kicksTotal) > 0 ? Number(metrics.pressuredKicks || 0) / Number(metrics.kicksTotal) : null), 1);
    const keeperSharePct = formatPercentOrNA(derived?.keeperShare, 1);

    const positionLine = formatPositionLine(summary);
    const shapeLine = formatTeamShapeLines(teamPlayerSummaries, teamKey);
    const opponentShapeLine = formatTeamShapeLines(teamPlayerSummaries, opponentKey);
    const friendlyWingLines = formatWingSummaryLines(teamPlayerSummaries, teamKey, friendlyTeamName);
    const opponentWingLines = formatWingSummaryLines(teamPlayerSummaries, opponentKey, opponentTeamName);

    const detailedLines = [
      `- Overview: Haxball Impact rating ${formatNumber(Number.isFinite(Number(summary.ruleRating)) ? summary.ruleRating : (metrics.ruleBaseline ?? metrics.ruleRating), 2)}, Role ${summary.role || 'N/A'}, Team ${friendlyTeamName}.`,
      positionLine,
      shapeLine,
      `- Production: Goals ${formatCount(metrics.goals)}, Assists ${formatCount(metrics.assists)}, Shots ${formatCount(metrics.shotsOnTarget)}/${formatCount(metrics.shotsTotal)} on target, xG ${formatNumber(metrics.xg, 2)} (on target ${formatNumber(metrics.xgOnTarget, 2)}), Saves ${formatCount(metrics.saves)}, xG prevented ${formatNumber(metrics.xgPrevented, 2)}.`,
      (passStats.isReliable && passAttempts > 0
        ? `- Passing & creation: ${formatCount(passesCompleted)} of ${formatCount(passAttempts)} passes found a teammate, Key passes ${formatCount(metrics.keyPasses)}, Progressive passes ${formatCount(metrics.progressivePasses)} (gain ${formatNumber(metrics.progressiveDistance, 0)}), Final-third entries ${formatCount(metrics.finalThirdEntries)}, Chain touches ${formatCount(metrics.goalChainTouches)}, Secondary assists ${formatCount(metrics.secondaryAssistsChain)}, Third assists ${formatCount(metrics.tertiaryAssistsChain)}, Direction split Fwd ${passesForward} / Mid ${passesLateral} / Back ${passesBackward}.`
        : `- Passing & creation: ${formatCount(passesCompleted)} completed passes (see Player Stats for accuracy), Key passes ${formatCount(metrics.keyPasses)}, Progressive passes ${formatCount(metrics.progressivePasses)} (gain ${formatNumber(metrics.progressiveDistance, 0)}), Final-third entries ${formatCount(metrics.finalThirdEntries)}, Chain touches ${formatCount(metrics.goalChainTouches)}, Secondary assists ${formatCount(metrics.secondaryAssistsChain)}, Third assists ${formatCount(metrics.tertiaryAssistsChain)}, Direction split Fwd ${passesForward} / Mid ${passesLateral} / Back ${passesBackward}.`),
      `- On the ball: Touches ${formatCount(metrics.touches)}, Possession touches ${formatCount(metrics.possessionTouches)}, Starts ${formatCount(metrics.possessionStarts)}, Scoring touches ${formatCount(metrics.scoringPossessionTouches)}, Tough touches ${formatCount(metrics.pressuredPossessionTouches)}, Calm touches ${pressureReliefPct}, Kicks ${formatCount(metrics.kicksTotal)} (under pressure ${pressureRatePct}, avg speed ${formatNumber(metrics.avgKickSpeed, 1)}).`,
      `- Transition: Fast breaks ${formatCount(metrics.counterRuns)}, Steals ${formatCount(metrics.forcedTurnovers)}, Lost balls ${formatCount(metrics.turnoversCommitted)} (risky ${formatCount(metrics.dangerousTurnovers)}, hot touches ${formatCount(metrics.turnoverTouches)}).`,
      `- Duels & defence: Duels ${formatCount(metrics.duelsWon)}/${formatCount(metrics.duelsTotal)} (win rate ${duelWinRate}), Open duels ${formatCount(metrics.duelsOpen)}, Corner duels ${formatCount(metrics.duelsCorner)}, Intercept duels ${formatCount(metrics.duelsInterception)}, Interceptions ${formatCount(metrics.interceptions)}, Clearances ${formatCount(metrics.clearances)} (events ${formatCount(metrics.clearanceEvents)}, follow-ups ${formatCount(metrics.clearanceRecoveries)}), Ball wins ${formatCount(metrics.recoveries)} (high wins ${formatCount(metrics.finalThirdRecoveries)}), Last-man stops ${formatCount(metrics.lastDefenderEvents)}, Sweeper moves ${formatCount(metrics.sweeperActions)}, Bumps ${formatCount(metrics.bumps)}, Rebounds ${formatCount(metrics.rebounds)} (won ${formatCount(metrics.reboundRecoveries)}).`,
      `- Goal keeping: GK ticks ${formatCount(metrics.gkTicks)} (${formatNumber(gkMinutes, 2)} min, share ${keeperSharePct}), xG faced ${formatNumber(metrics.xgFaced, 2)}, Goals allowed ${formatCount(metrics.goalsConceded)}, Saves each minute ${formatNumber(metrics.savesPerMinute, 2)}, xG prevented ${formatNumber(metrics.xgPrevented, 2)}.`,
      `- Extra impact: Depth impact ${Number.isFinite(depthImpact) ? formatNumber(depthImpact, 2) : 'n/a'}, Possession boost ${formatNumber(derived?.possessionImpact ?? 0, 2)}, Progressive passes/min ${formatNumber(derived?.progressivePassesPerMinute ?? 0, 2)}, Steals/min ${formatNumber(derived?.forcedTurnoversPerMinute ?? 0, 2)}, Fast breaks/min ${formatNumber(derived?.counterRunsPerMinute ?? 0, 2)}, Sweeper moves/min ${formatNumber(derived?.sweeperActionsPerMinute ?? 0, 2)}.`,
    ];

    if (!passStats.isReliable) {
      detailedLines.splice(3, 0, '- Pass accuracy note: Attempt counts were not captured separately in this replay; refer to the Player Stats table for any available accuracy info.');
    }

    const detailedOutput = detailedLines.filter(Boolean);

    const teammateLines = teamPlayerSummaries
      .filter((item) => item.nick !== selectedPlayer && (item.team || '').toLowerCase() === teamKey)
      .map((item) => {
        const m = item.metrics || {};
        const derivedMate = getDerivedFor(item.nick);
        const matePassStats = computePassStats(item, derivedMate);
        const matePassAttempts = matePassStats.attempts || Number(m.passesAttempted) || 0;
        const matePassCompleted = matePassStats.completed || Number(m.passesCompleted) || 0;
        const matePassSummary = matePassStats.isReliable && matePassAttempts > 0
          ? `${formatCount(matePassCompleted)} of ${formatCount(matePassAttempts)} attempts`
          : `${formatCount(matePassCompleted)} completed (see Player Stats for accuracy)`;
        const mateDuelWin = formatPercentOrNA(derivedMate?.duelWinRate ?? (Number(m.duelsTotal) > 0 ? Number(m.duelsWon) / Number(m.duelsTotal) : null), 1);
        const matePressureRelief = formatPercentOrNA(derivedMate?.pressureRelief ?? (Number(m.kicksTotal) > 0 ? (Number(m.kicksTotal) - Number(m.pressuredKicks || 0)) / Number(m.kicksTotal) : null), 1);
        const mateKeeperShare = formatPercentOrNA(derivedMate?.keeperShare, 1);
        const mateGkMinutes = formatNumber((Number(m.gkTicks) || 0) / 3600, 2);
        return `- ${item.nick}: rating ${formatNumber(Number.isFinite(Number(item.ruleRating)) ? item.ruleRating : (m.ruleBaseline ?? m.ruleRating), 2)}, Goals ${formatCount(m.goals)}, Assists ${formatCount(m.assists)}, Shots ${formatCount(m.shotsOnTarget)}/${formatCount(m.shotsTotal)}, xG ${formatNumber(m.xg, 2)}, Passes ${matePassSummary}, Key passes ${formatCount(m.keyPasses)}, Progressive passes ${formatCount(m.progressivePasses)}, Direction F/L/B ${formatCount(m.passesForward)}/${formatCount(m.passesLateral)}/${formatCount(m.passesBackward)}, Possession touches ${formatCount(m.possessionTouches)}, Pressured touches ${formatCount(m.pressuredPossessionTouches)}, Pressure relief ${matePressureRelief}, Turnovers ${formatCount(m.turnoversCommitted)} (dangerous ${formatCount(m.dangerousTurnovers)}), Forced turnovers ${formatCount(m.forcedTurnovers)}, Counter runs ${formatCount(m.counterRuns)}, Duels ${formatCount(m.duelsWon)}/${formatCount(m.duelsTotal)} (${mateDuelWin}), Open ${formatCount(m.duelsOpen)}, Corner ${formatCount(m.duelsCorner)}, Interception ${formatCount(m.duelsInterception)}, Interceptions ${formatCount(m.interceptions)}, Recoveries ${formatCount(m.recoveries)} (final-third ${formatCount(m.finalThirdRecoveries)}), Clearances ${formatCount(m.clearances)} (events ${formatCount(m.clearanceEvents)}, recovers ${formatCount(m.clearanceRecoveries)}), Rebounds ${formatCount(m.rebounds)} (recovers ${formatCount(m.reboundRecoveries)}), GK ticks ${formatCount(m.gkTicks)} (${mateGkMinutes} min, share ${mateKeeperShare}), xG faced ${formatNumber(m.xgFaced, 2)}, Goals conceded ${formatCount(m.goalsConceded)}, Saves ${formatCount(m.saves)}, xG prevented ${formatNumber(m.xgPrevented, 2)}.`;
      }).join('\n');

    const opponentLines = teamPlayerSummaries
      .filter((item) => (item.team || '').toLowerCase() === opponentKey)
      .map((item) => {
        const m = item.metrics || {};
        const derivedOpp = getDerivedFor(item.nick);
        const oppPassStats = computePassStats(item, derivedOpp);
        const oppPassAttempts = oppPassStats.attempts || Number(m.passesAttempted) || 0;
        const oppPassCompleted = oppPassStats.completed || Number(m.passesCompleted) || 0;
        const oppPassSummary = oppPassStats.isReliable && oppPassAttempts > 0
          ? `${formatCount(oppPassCompleted)} of ${formatCount(oppPassAttempts)} attempts`
          : `${formatCount(oppPassCompleted)} completed (see Player Stats for accuracy)`;
        const oppDuelWin = formatPercentOrNA(derivedOpp?.duelWinRate ?? (Number(m.duelsTotal) > 0 ? Number(m.duelsWon) / Number(m.duelsTotal) : null), 1);
        const oppPressureRelief = formatPercentOrNA(derivedOpp?.pressureRelief ?? (Number(m.kicksTotal) > 0 ? (Number(m.kicksTotal) - Number(m.pressuredKicks || 0)) / Number(m.kicksTotal) : null), 1);
        const oppKeeperShare = formatPercentOrNA(derivedOpp?.keeperShare, 1);
        const oppGkMinutes = formatNumber((Number(m.gkTicks) || 0) / 3600, 2);
        return `- ${item.nick}: rating ${formatNumber(Number.isFinite(Number(item.ruleRating)) ? item.ruleRating : (m.ruleBaseline ?? m.ruleRating), 2)}, Goals ${formatCount(m.goals)}, Assists ${formatCount(m.assists)}, Shots ${formatCount(m.shotsOnTarget)}/${formatCount(m.shotsTotal)}, xG ${formatNumber(m.xg, 2)}, Passes ${oppPassSummary}, Key passes ${formatCount(m.keyPasses)}, Progressive passes ${formatCount(m.progressivePasses)}, Direction F/L/B ${formatCount(m.passesForward)}/${formatCount(m.passesLateral)}/${formatCount(m.passesBackward)}, Possession touches ${formatCount(m.possessionTouches)}, Pressured touches ${formatCount(m.pressuredPossessionTouches)}, Pressure relief ${oppPressureRelief}, Turnovers ${formatCount(m.turnoversCommitted)} (dangerous ${formatCount(m.dangerousTurnovers)}), Forced turnovers ${formatCount(m.forcedTurnovers)}, Counter runs ${formatCount(m.counterRuns)}, Duels ${formatCount(m.duelsWon)}/${formatCount(m.duelsTotal)} (${oppDuelWin}), Open ${formatCount(m.duelsOpen)}, Corner ${formatCount(m.duelsCorner)}, Interception ${formatCount(m.duelsInterception)}, Interceptions ${formatCount(m.interceptions)}, Recoveries ${formatCount(m.recoveries)} (final-third ${formatCount(m.finalThirdRecoveries)}), Clearances ${formatCount(m.clearances)} (events ${formatCount(m.clearanceEvents)}, recovers ${formatCount(m.clearanceRecoveries)}), Rebounds ${formatCount(m.rebounds)} (recovers ${formatCount(m.reboundRecoveries)}), GK ticks ${formatCount(m.gkTicks)} (${oppGkMinutes} min, share ${oppKeeperShare}), xG faced ${formatNumber(m.xgFaced, 2)}, Goals conceded ${formatCount(m.goalsConceded)}, Saves ${formatCount(m.saves)}, xG prevented ${formatNumber(m.xgPrevented, 2)}.`;
      }).join('\n');

    const possessionShare = analysis?.possessionShare || {};
    const territoryShare = analysis?.territoryShare || {};
    const teamXgTotals = analysis?.teamXgTotals || {};
    const teamSaveTotals = analysis?.teamSaveTotals || {};

    const contextSections = [
      `Score: ${teamDisplayNames.red} ${formatCount(scoreRed)} - ${formatCount(scoreBlue)} ${teamDisplayNames.blue}. Duration ${formatNumber(minutes, 1)} minutes.`,
      `Team view: Possession ${teamDisplayNames.red} ${formatPercent(possessionShare?.red ?? 0, 1)} vs ${teamDisplayNames.blue} ${formatPercent(possessionShare?.blue ?? 0, 1)}, Territory ${teamDisplayNames.red} ${formatPercent(territoryShare?.red ?? 0, 1)} vs ${teamDisplayNames.blue} ${formatPercent(territoryShare?.blue ?? 0, 1)}, Team xG ${teamDisplayNames.red} ${formatNumber(teamXgTotals?.red, 2)} vs ${teamDisplayNames.blue} ${formatNumber(teamXgTotals?.blue, 2)}, Team saves ${teamDisplayNames.red} ${formatCount(teamSaveTotals?.red)} vs ${teamDisplayNames.blue} ${formatCount(teamSaveTotals?.blue)}.`,
      `Selected player focus (${selectedPlayer} - ${friendlyTeamName}):\n${detailedOutput.join('\n')}`,
      teammateLines ? `${friendlyTeamName} teammates:\n${teammateLines}` : '',
      friendlyWingLines,
      opponentShapeLine,
      opponentWingLines,
      opponentLines ? `${opponentTeamName} lineup:\n${opponentLines}` : '',
      'Data source: match scoreboard and full replay metrics.'
    ];

    return contextSections.filter(Boolean).join('\n\n');
  }, [analysis, match, playerSummaries, selectedPlayer, teamDisplayNames, teamPlayerSummaries, getDerivedFor]);

  useEffect(() => {
    setMessages([]);
    setInput('');
    setError(null);
    if (!selectedPlayer) return;
    const teamKey = selectedSummary?.team && typeof selectedSummary.team === 'string' && selectedSummary.team.toLowerCase() === 'blue' ? 'blue' : 'red';
    const teamLabel = selectedSummary ? (teamDisplayNames[teamKey] || teamKey.toUpperCase()) : 'your team';
    const greeting = `Hi ${selectedPlayer}! I'm Hax GPT. I can help you break down this match, highlight strengths, and spot ways to improve for ${teamLabel}. Ask me anything about your performance or stats.`;
    setMessages([{ role: 'assistant', content: greeting }]);
  }, [selectedPlayer, selectedSummary, teamDisplayNames]);

  const messagesEndRef = useRef(null);
  useEffect(() => {
    if (!messagesEndRef.current) return;
    try {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    } catch (_) {}
  }, [messages]);

  const handleSelectPlayer = useCallback((nick) => {
    setSelectedPlayer(nick);
  }, []);

  const handleChangePlayer = useCallback(() => {
    if (isLoading) return;
    setSelectedPlayer(null);
    setInput('');
    setMessages([]);
    setError(null);
  }, [isLoading]);

  const handleSend = useCallback(async () => {
    if (!selectedPlayer || isLoading) return;
    const trimmed = input.trim();
    if (!trimmed) return;
    const userMessage = { role: 'user', content: trimmed };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput('');
    setIsLoading(true);
    setError(null);
    try {
      const assistantReply = await sendHaxGptChat({
        systemPrompt: SYSTEM_PROMPT,
        context: matchContext,
        messages: nextMessages
      });
      const replyContent = assistantReply || 'I had trouble understanding that. Could you try asking another way?';
      setMessages((prev) => [...prev, { role: 'assistant', content: replyContent }]);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to reach Hax GPT.');
      setMessages((prev) => [...prev, { role: 'assistant', content: 'I could not contact the coaching service right now. Please try again in a moment.' }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, matchContext, messages, selectedPlayer]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  if (!match) {
    return (
      <Box className="gs-haxgpt" sx={{ gap: 2 }}>
        <Typography variant="body1">Load a replay to start using Hax GPT.</Typography>
      </Box>
    );
  }

  if (!selectedPlayer) {
    return (
      <Box className="gs-haxgpt" sx={{ gap: 3 }}>
        <Box className="gs-haxgpt-question">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Which player are you?</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.75)' }}>
            Pick your in-game name so Hax GPT can tailor advice to you.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} className="gs-haxgpt-player-groups">
          <Box className="gs-haxgpt-team-card red">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Red Team</Typography>
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {redPlayers.length ? (
                redPlayers.map((nick) => (
                  <Button key={nick} variant="contained" color="error" size="small" onClick={() => handleSelectPlayer(nick)}>
                    {nick}
                  </Button>
                ))
              ) : (
                <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.6)' }}>
                  No red players detected.
                </Typography>
              )}
            </Stack>
          </Box>
          <Box className="gs-haxgpt-team-card blue">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Blue Team</Typography>
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {bluePlayers.length ? (
                bluePlayers.map((nick) => (
                  <Button key={nick} variant="contained" color="primary" size="small" onClick={() => handleSelectPlayer(nick)}>
                    {nick}
                  </Button>
                ))
              ) : (
                <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.6)' }}>
                  No blue players detected.
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className="gs-haxgpt" sx={{ gap: 2.5 }}>
      <Box className="gs-haxgpt-header">
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Chatting as {selectedPlayer}</Typography>
        <Button variant="text" size="small" onClick={handleChangePlayer} disabled={isLoading}>
          Choose a different player
        </Button>
      </Box>
      {error && (
        <Box className="gs-haxgpt-error" role="alert">
          {error}
        </Box>
      )}
      <Box className="gs-haxgpt-chat">
        <Box className="gs-haxgpt-messages">
          {messages.map((message, idx) => {
            const rendered = renderMessageContent(message.content, `msg-${idx}`);
            return (
              <Box key={`msg-${idx}`} className={`gs-haxgpt-message ${message.role}`} sx={{ '& > :last-child': { mb: 0 } }}>
                {rendered || (
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {message?.content || ''}
                  </Typography>
                )}
              </Box>
            );
          })}
          {isLoading && (
            <Box className="gs-haxgpt-message assistant" sx={{ alignItems: 'center' }}>
              <CircularProgress size={18} sx={{ color: 'rgba(148,163,184,0.9)' }} />
              <Typography variant="body2" sx={{ ml: 1 }}>Thinking...</Typography>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>
        <Box className="gs-haxgpt-input">
          <TextField
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask for coaching tips or clarification on your stats..."
            multiline
            minRows={1}
            maxRows={5}
            fullWidth
            variant="outlined"
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(15, 23, 42, 0.75)',
                color: '#e2e8f0'
              }
            }}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            sx={{ mt: 1, alignSelf: 'flex-end' }}
          >
            Send
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
